import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Helmet} from "react-helmet";
import {useIntl} from "react-intl";
import {Spinner} from "../../../shared";
import useBestVideoServer from "../../hooks/useBestVideoServer";
import useEpisodeDetailsData from "../../hooks/useEpisodeDetailsData";
import useViewTMDBContent from "../../hooks/useViewTMDBContent";
import UseUserPreferences from "../../../user-app-config/hooks/use-user-preferences";
import TmdbLikeItem from "../../movies/tmdbLikeItem/tmdb-like-item";
import SpoilersPreferenceModal from "../../movies/spoilers-preference-modal/spoilers-preference-modal";
import TvModal from "../components/TvModal";
import useTvStreamResolver, {StreamOption} from "../hooks/useTvStreamResolver";
import UseSubtitles from "../../squadflixVideoView/customFilePlayer/hooks/useSubtitles";
import {SquadflixLogsApi} from "../../../../apis/squadflix-logs/SquadflixLogsApi";
import {SquadflixSocketEmitEvents} from "../../../app/squadflix-tv-mode/socket-utils/socket-send-events";
import "/node_modules/flag-icons/css/flag-icons.min.css";
import "../../squadflixVideoView/customFilePlayer/customFilePlayer.css";

function formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds) || seconds < 0) return "00:00";
    const totalSecs = Math.floor(seconds);
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    if (hrs > 0) {
        return `${hrs}:${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
    }
    return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
}

function getCleanVideoUrl(url: string): string {
    if (!url) return "";
    return url.split("&t=")[0];
}

function getCurrentTimeFromVideoUrl(url: string): number {
    if (!url) return 0;
    const parts = url.split("&t=");
    if (parts.length < 2) return 0;
    const timeStr = parts[1].split("&")[0];
    const parsed = parseFloat(timeStr);
    return isNaN(parsed) ? 0 : Math.floor(parsed);
}

export type SquadflixTvPlayerViewProps = {
    imagePath?: string;
    tagline?: string;
    overview?: string;
    view?: boolean;
    playing?: boolean;
    itemName?: string;
    item: any;
    file?: any;
    type: "movie" | "tv";
    tmdbId: string | number;
    season?: number;
    episode?: number;
    nextEpisode?: any;
    onGoNextEpisode?: () => void;
    squadFlixTvHook?: any;
    onBack?: () => void;
};

export const SquadflixTvPlayerView: React.FC<SquadflixTvPlayerViewProps> = (props) => {
    const {
        view,
        item,
        file,
        type,
        tmdbId,
        season,
        episode,
        nextEpisode,
        onGoNextEpisode,
        onBack,
        squadFlixTvHook
    } = props;

    const navigate = useNavigate();
    const {validServers, selectedServer, setSelectedServer, usingServer} = useBestVideoServer(item, file, type);
    const {userPreferences, loading: loadingPreferences} = UseUserPreferences();
    const {markThingAsView} = useViewTMDBContent();

    // Stream resolver for all servers (SquadFlix, AIOStream, TowersStream, VerOnline, embeds)
    const {
        loading: loadingStream,
        error: streamError,
        videoUrl,
        originalVideoUrl,
        iframeSrc,
        duration: streamDuration,
        subtitles,
        audios,
        streams,
        selectedStream,
        selectStream,
        downloadInfo,
        isStreaming
    } = useTvStreamResolver(selectedServer, item, file, type, tmdbId, season, episode);

    // Video Mode: "streaming" (transcoded for compatibility) vs "lossless" (original direct file)
    const [videoMode, setVideoMode] = useState<"streaming" | "lossless">(() => {
        const itemPref = localStorage.getItem(`preferedVideoType__${item?.serie?.id ?? item?.id ?? tmdbId}`);
        if (itemPref === "streaming" || itemPref === "original") {
            return itemPref === "original" ? "lossless" : "streaming";
        }
        const globalPref = localStorage.getItem("tv_prefered_video_mode");
        if (globalPref === "lossless" || globalPref === "streaming") {
            return globalPref;
        }
        return "streaming";
    });
    const [videoModeModalOpen, setVideoModeModalOpen] = useState(false);

    // Determine active streaming vs lossless mode
    const isLosslessActive = videoMode === "lossless" && !!originalVideoUrl;
    const isStreamingActive = isStreaming && !isLosslessActive;
    const activeVideoUrl = isLosslessActive ? originalVideoUrl : videoUrl;

    // Player state
    const videoRef = useRef<HTMLVideoElement>(null);
    const subtitlesContainerRef = useRef<HTMLDivElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(streamDuration || (item?.runtime ? item.runtime * 60 : 0));
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [videoSrc, setVideoSrc] = useState<string | null>(null);
    const [isVideoBuffering, setIsVideoBuffering] = useState(false);
    const streamingStartingTime = useRef<number>(0);

    // Selected Audio & Subtitle tracks
    const [selectedAudioId, setSelectedAudioId] = useState<string | number | null>(null);

    // Modals
    const [serversModalOpen, setServersModalOpen] = useState(false);
    const [streamsModalOpen, setStreamsModalOpen] = useState(false);
    const [subtitlesModalOpen, setSubtitlesModalOpen] = useState(false);
    const [audiosModalOpen, setAudiosModalOpen] = useState(false);

    // Fullscreen state and handler
    const [isFullscreen, setIsFullscreen] = useState(
        () => !!(document.fullscreenElement || (document as any).webkitFullscreenElement)
    );

    useEffect(() => {
        const handleFsChange = () => {
            setIsFullscreen(!!(document.fullscreenElement || (document as any).webkitFullscreenElement));
        };
        document.addEventListener("fullscreenchange", handleFsChange);
        document.addEventListener("webkitfullscreenchange", handleFsChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleFsChange);
            document.removeEventListener("webkitfullscreenchange", handleFsChange);
        };
    }, []);

    const toggleFullscreen = useCallback(async () => {
        try {
            const fsElement = document.fullscreenElement || (document as any).webkitFullscreenElement;
            if (!fsElement) {
                if (document.documentElement.requestFullscreen) {
                    await document.documentElement.requestFullscreen();
                } else if ((document.documentElement as any).webkitRequestFullscreen) {
                    await (document.documentElement as any).webkitRequestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if ((document as any).webkitExitFullscreen) {
                    await (document as any).webkitExitFullscreen();
                }
            }
        } catch (err) {
            console.error("Error toggling fullscreen", err);
        }
    }, []);

    // OSD visibility & auto-hide
    const [osdVisible, setOsdVisible] = useState(true);
    const hideTimerRef = useRef<any>(null);
    const [seekFlash, setSeekFlash] = useState<{text: string; icon: string} | null>(null);
    const flashTimerRef = useRef<any>(null);

    const isAnyModalOpen =
        serversModalOpen || streamsModalOpen || subtitlesModalOpen || audiosModalOpen || videoModeModalOpen;

    const hideSpoilers =
        (userPreferences.hideSpoilers || userPreferences.hideSpoilers == null || loadingPreferences) &&
        !view &&
        type === "tv";

    const displayTitle = hideSpoilers
        ? `${item?.serie?.name || ""} — S${season}:E${episode}`
        : type === "tv"
        ? `${item?.serie?.name ? item.serie.name + " • " : ""}S${season}:E${episode} — ${item?.name || item?.title || ""}`
        : item?.title || item?.name || "";

    // Subtitles Hook
    const {
        currentSubtitle,
        setCurrentSubtitle,
        availableSubtitles
    } = UseSubtitles({
        subtitlesContainer: subtitlesContainerRef,
        subtitles,
        currentTime,
        itemId: item?.id || tmdbId
    });

    const showOsd = useCallback(() => {
        setOsdVisible(true);
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        if (!isAnyModalOpen) {
            hideTimerRef.current = setTimeout(() => {
                if (videoRef.current && !videoRef.current.paused) {
                    setOsdVisible(false);
                }
            }, 3800);
        }
    }, [isAnyModalOpen]);

    // Keep OSD visible while paused or while modal is open
    useEffect(() => {
        if (!isPlaying || isAnyModalOpen) {
            setOsdVisible(true);
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        } else {
            showOsd();
        }
    }, [isPlaying, isAnyModalOpen, showOsd]);

    // Synchronize videoUrl and restore saved resume time
    const storageKey = `squadflix_time_${tmdbId}_${season || 0}_${episode || 0}`;
    const lastItemKeyRef = useRef<string>("");

    useEffect(() => {
        if (!activeVideoUrl) {
            setVideoSrc(null);
            return;
        }

        const currentItemKey = `${tmdbId}_${season || 0}_${episode || 0}`;
        let resumeSecs = 0;
        if (lastItemKeyRef.current !== currentItemKey) {
            lastItemKeyRef.current = currentItemKey;
            const saved = localStorage.getItem(storageKey);
            resumeSecs = saved && Number(saved) > 5 ? Number(saved) : 0;
        } else {
            resumeSecs = currentTime > 0 ? currentTime : 0;
        }

        if (isStreamingActive) {
            const clean = getCleanVideoUrl(activeVideoUrl);
            const audioParam = selectedAudioId != null ? `&audioIndex=${selectedAudioId}` : "";
            if (resumeSecs > 0) {
                streamingStartingTime.current = resumeSecs;
                setCurrentTime(resumeSecs);
                setVideoSrc(`${clean}&t=${resumeSecs.toFixed(2)}${audioParam}`);
            } else {
                streamingStartingTime.current = 0;
                setCurrentTime(0);
                setVideoSrc(audioParam ? `${clean}${audioParam}` : activeVideoUrl);
            }
        } else {
            streamingStartingTime.current = 0;
            setVideoSrc(activeVideoUrl);
            if (resumeSecs > 0 && videoRef.current) {
                videoRef.current.currentTime = resumeSecs;
                setCurrentTime(resumeSecs);
            }
        }
    }, [storageKey, activeVideoUrl, isStreamingActive, selectedAudioId, tmdbId, season, episode]);

    // Keep duration updated when resolver duration arrives
    useEffect(() => {
        if (streamDuration && streamDuration > 0) {
            setDuration(streamDuration);
        }
    }, [streamDuration]);

    // Cast Socket events
    useEffect(() => {
        if (!squadFlixTvHook?.connectedReceptor) return;
        SquadflixSocketEmitEvents.sendViewingItem(
            squadFlixTvHook.connectedReceptor,
            item,
            type === "tv" ? "serie" : "movie"
        );
        return () => {
            SquadflixSocketEmitEvents.sendStopViewingItem(squadFlixTvHook.connectedReceptor);
        };
    }, [item, squadFlixTvHook?.connectedReceptor, type]);

    // Periodic view logging to backend
    const lastViewingLogRef = useRef<number>(0);
    useEffect(() => {
        if (!currentTime || currentTime - lastViewingLogRef.current < 120) return;
        lastViewingLogRef.current = currentTime;
        SquadflixLogsApi.logSquadflixView({
            tmdbId: item?.serie?.id ?? item?.id ?? tmdbId,
            season: item?.season_number ?? season,
            episode: item?.episode_number ?? episode,
            duration: Math.floor(currentTime)
        }).catch(() => {});
    }, [currentTime, item, tmdbId, season, episode]);

    const handleGoBack = useCallback(() => {
        if (onBack) {
            onBack();
            return;
        }
        if (type === "movie") {
            navigate(`/squadflix/movies/${tmdbId}`);
        } else {
            navigate(`/squadflix/series/${tmdbId}`);
        }
    }, [navigate, onBack, tmdbId, type]);

    const triggerFlash = (text: string, icon: string) => {
        setSeekFlash({text, icon});
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setSeekFlash(null), 1200);
    };

    const handleSelectVideoMode = useCallback(
        (mode: "streaming" | "lossless") => {
            if (mode === videoMode) {
                setVideoModeModalOpen(false);
                return;
            }
            const targetUrl = mode === "lossless" ? originalVideoUrl : videoUrl;
            if (!targetUrl) {
                triggerFlash("Modo no disponible para este servidor", "fa-circle-exclamation");
                setVideoModeModalOpen(false);
                return;
            }
            localStorage.setItem("tv_prefered_video_mode", mode);
            localStorage.setItem(
                `preferedVideoType__${item?.serie?.id ?? item?.id ?? tmdbId}`,
                mode === "lossless" ? "original" : "streaming"
            );
            setVideoMode(mode);
            setVideoModeModalOpen(false);
            triggerFlash(
                mode === "lossless" ? "Modo Lossless (Calidad Original)" : "Modo Streaming (Mayor Compatibilidad)",
                mode === "lossless" ? "fa-gem" : "fa-bolt"
            );
        },
        [item, originalVideoUrl, tmdbId, videoMode, videoUrl]
    );

    const togglePlayPause = () => {
        const vid = videoRef.current;
        if (!vid) return;
        if (vid.paused) {
            vid.play().catch(() => {});
            setIsPlaying(true);
            triggerFlash("Reproduciendo", "fa-play");
            showOsd();
        } else {
            vid.pause();
            setIsPlaying(false);
            triggerFlash("Pausa", "fa-pause");
            setOsdVisible(true);
        }
    };

    const setVideoCurrentTime = useCallback(
        (targetTime: number) => {
            const vid = videoRef.current;
            if (!vid) return;

            const maxTotal = duration > 0 ? duration : (streamDuration > 0 ? streamDuration : 100000);
            const clamped = Math.max(0, Math.min(maxTotal, targetTime));

            if (isStreamingActive) {
                setIsVideoBuffering(true);
                const clean = getCleanVideoUrl(videoSrc || activeVideoUrl || "");
                const audioParam = selectedAudioId != null ? `&audioIndex=${selectedAudioId}` : "";
                const newUrl = `${clean}&t=${parseInt(String(clamped)).toFixed(2)}${audioParam}`;
                streamingStartingTime.current = parseInt(String(clamped));
                setVideoSrc(newUrl);
                vid.src = newUrl;
                vid.load();
                vid.play().catch(() => {});
            } else {
                vid.currentTime = clamped;
            }

            setCurrentTime(clamped);
            localStorage.setItem(storageKey, String(Math.floor(clamped)));
            showOsd();
        },
        [activeVideoUrl, duration, isStreamingActive, selectedAudioId, showOsd, storageKey, streamDuration, videoSrc]
    );

    const handleSeek = (delta: number) => {
        const target = currentTime + delta;
        setVideoCurrentTime(target);
        triggerFlash(
            `${delta > 0 ? "+10s" : "-10s"} (${formatTime(Math.max(0, target))})`,
            delta > 0 ? "fa-rotate-right" : "fa-rotate-left"
        );
    };

    const handleAudioTrackChange = (track: any) => {
        setSelectedAudioId(track.id);
        setAudiosModalOpen(false);
        const vid = videoRef.current;
        if (!vid) return;

        if (isStreamingActive) {
            setIsVideoBuffering(true);
            const clean = getCleanVideoUrl(videoSrc || activeVideoUrl || "");
            const newUrl = `${clean}&audioIndex=${track.id}&t=${Math.floor(currentTime).toFixed(2)}`;
            streamingStartingTime.current = Math.floor(currentTime);
            setVideoSrc(newUrl);
            vid.src = newUrl;
            vid.load();
            vid.play().catch(() => {});
        } else if ((vid as any).audioTracks) {
            Array.from((vid as any).audioTracks).forEach((t: any) => {
                t.enabled = t.id === track.id;
            });
        }
    };

    // Remote Keyboard listener
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (isAnyModalOpen) return;

            if (e.key === "f" || e.key === "F") {
                e.preventDefault();
                toggleFullscreen();
                return;
            }

            // When OSD is hidden, direct TV shortcuts for quick seek & play/pause
            if (!osdVisible) {
                if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    handleSeek(-10);
                    return;
                }
                if (e.key === "ArrowRight") {
                    e.preventDefault();
                    handleSeek(10);
                    return;
                }
                if (e.key === "Enter" || e.key === " " || e.key === "MediaPlayPause") {
                    e.preventDefault();
                    togglePlayPause();
                    return;
                }
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                    showOsd();
                    return;
                }
                if (e.key === "Escape" || e.keyCode === 461 || e.keyCode === 10009 || e.key === "Backspace") {
                    e.preventDefault();
                    handleGoBack();
                    return;
                }
            } else {
                // When OSD is visible, any key resets the auto-hide timer
                showOsd();

                if (e.key === "Escape" || e.keyCode === 461 || e.keyCode === 10009) {
                    e.preventDefault();
                    setOsdVisible(false);
                } else if (e.key === "MediaPlayPause") {
                    e.preventDefault();
                    togglePlayPause();
                } else if (e.key === "MediaRewind") {
                    e.preventDefault();
                    handleSeek(-15);
                } else if (e.key === "MediaFastForward") {
                    e.preventDefault();
                    handleSeek(15);
                }
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [handleGoBack, isAnyModalOpen, osdVisible, showOsd, toggleFullscreen]);

    return (
        <div
            className="tv-player-screen"
            onMouseMove={showOsd}
            onClick={showOsd}>
            {/* 1. Full-screen Video Canvas (100vw x 100vh) */}
            {iframeSrc ? (
                <iframe
                    className="tv-player-screen__iframe"
                    title={displayTitle}
                    src={iframeSrc}
                    allowFullScreen
                />
            ) : (videoSrc || activeVideoUrl) ? (
                <video
                    ref={videoRef}
                    id="squadflix-tv-player-video"
                    className="tv-player-screen__video"
                    src={videoSrc || activeVideoUrl || ""}
                    autoPlay
                    playsInline
                    muted={isMuted}
                    onLoadStart={() => setIsVideoBuffering(true)}
                    onLoadedData={(e) => {
                        if (isStreamingActive) {
                            streamingStartingTime.current = getCurrentTimeFromVideoUrl(e.currentTarget.src);
                        } else if (currentTime > 0) {
                            e.currentTarget.currentTime = currentTime;
                        }
                        setIsVideoBuffering(false);
                    }}
                    onCanPlay={() => setIsVideoBuffering(false)}
                    onWaiting={() => setIsVideoBuffering(true)}
                    onPlaying={() => {
                        setIsVideoBuffering(false);
                        setIsPlaying(true);
                    }}
                    onPlay={() => {
                        setIsPlaying(true);
                        setIsVideoBuffering(false);
                    }}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => {
                        markThingAsView(true);
                        if (nextEpisode && onGoNextEpisode) {
                            onGoNextEpisode();
                        }
                    }}
                    onError={(e) => {
                        console.error("[TvPlayer] Video playback error", e);
                        setIsVideoBuffering(false);
                        if (videoMode === "lossless" && videoUrl) {
                            triggerFlash("Formato no compatible, cambiando a Streaming...", "fa-triangle-exclamation");
                            handleSelectVideoMode("streaming");
                        } else if (videoMode === "streaming" && originalVideoUrl) {
                            triggerFlash("Error en streaming, probando modo Lossless...", "fa-triangle-exclamation");
                            handleSelectVideoMode("lossless");
                        }
                    }}
                    onTimeUpdate={(e) => {
                        if (isVideoBuffering) setIsVideoBuffering(false);
                        const cur = e.currentTarget.currentTime + (isStreamingActive ? streamingStartingTime.current : 0);
                        setCurrentTime(cur);

                        // When streaming, duration comes from streamDuration (in seconds)
                        // because e.currentTarget.duration is only the chunk length (e.g. 18s)
                        const total = isStreamingActive
                            ? streamDuration
                            : (e.currentTarget.duration || streamDuration || 0);

                        if (total && total > 0) {
                            setDuration(total);
                        }

                        if (Math.floor(cur) % 5 === 0) {
                            localStorage.setItem(storageKey, String(Math.floor(cur)));
                        }
                    }}
                />
            ) : null}

            {/* Custom Subtitles rendered above video */}
            <div ref={subtitlesContainerRef} className="customFilePlayer__subtitles" />

            {/* 2. Center Floating Loading & Seek Flash Pills */}
            {((loadingStream && !videoSrc && !activeVideoUrl) || (isVideoBuffering && !isPlaying && !iframeSrc) || downloadInfo?.downloading) && (
                <div className="tv-player-screen__center-loading">
                    <Spinner />
                    <span className="tv-player-screen__center-loading-text">
                        {downloadInfo?.downloading
                            ? `Descargando torrent (${((downloadInfo?.progress || 0) * 100).toFixed(1)}%)...`
                            : "Cargando señal de vídeo..."}
                    </span>
                </div>
            )}

            {streamError && (
                <div className="tv-player-screen__center-error">
                    <i className="fa-solid fa-triangle-exclamation fa-2x text-warning mb-3"></i>
                    <h4>No se pudo reproducir este stream</h4>
                    <p className="text-muted">{streamError}</p>
                    <div className="d-flex gap-3 mt-3">
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className="tv-btn tv-btn--primary"
                            onClick={() => setServersModalOpen(true)}>
                            <i className="fa-solid fa-server pe-2"></i>Cambiar de servidor
                        </button>
                        {streams.length > 0 && (
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--glass"
                                onClick={() => setStreamsModalOpen(true)}>
                                <i className="fa-solid fa-bolt pe-2"></i>Elegir otro enlace
                            </button>
                        )}
                    </div>
                </div>
            )}

            {seekFlash && (
                <div className="tv-player-screen__seek-flash">
                    <i className={`fa-solid ${seekFlash.icon} pe-3 text-danger`}></i>
                    <span>{seekFlash.text}</span>
                </div>
            )}

            {/* 3. Floating TV OSD (Auto-hiding on playback) */}
            <div className={`tv-player-screen__osd ${osdVisible ? "is-visible" : ""}`}>
                {/* Top OSD Bar */}
                <header className="tv-player-screen__osd-top" data-tv-row="tv-player-osd-top">
                    <div className="tv-player-screen__osd-left">
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className="tv-btn tv-btn--glass tv-btn--icon"
                            onClick={handleGoBack}
                            title="Volver (Atrás)">
                            <i className="fa-solid fa-arrow-left"></i>
                        </button>
                        <div className="tv-player-screen__titles">
                            <span className="tv-player-screen__badge">
                                {type === "movie"
                                    ? "Película"
                                    : `Temporada ${season} • Episodio ${episode}`}
                            </span>
                            <h2 className="tv-player-screen__title">{displayTitle}</h2>
                        </div>
                    </div>

                    <div className="tv-player-screen__osd-right">
                        {/* Server Switcher Pill */}
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className="tv-btn tv-btn--glass"
                            onClick={() => setServersModalOpen(true)}>
                            <i className="fa-solid fa-server pe-2 text-warning"></i>
                            <span>
                                Servidor: <strong>{selectedServer || "Cargando..."}</strong>
                            </span>
                            {usingServer.flag && <>&nbsp;<span className={usingServer.flag}></span></>}
                            {usingServer.value === "SquadFlix" && <>&nbsp;<span>🌟</span></>}
                            <i className="fa-solid fa-chevron-down ps-2"></i>
                        </button>

                        {/* Video Mode Selector Pill (Streaming vs Lossless) */}
                        {originalVideoUrl && videoUrl && (
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--glass"
                                onClick={() => setVideoModeModalOpen(true)}
                                title="Cambiar modo de reproducción (Streaming / Lossless)">
                                <i className={`fa-solid ${videoMode === "lossless" ? "fa-gem text-info" : "fa-bolt text-warning"} pe-2`}></i>
                                <span>
                                    Modo: <strong>{videoMode === "lossless" ? "Lossless" : "Streaming"}</strong>
                                </span>
                                <i className="fa-solid fa-chevron-down ps-2"></i>
                            </button>
                        )}

                        {/* Stream / Release selector (for AIOStream, TowersStream, VerOnline) */}
                        {streams.length > 0 && (
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--glass"
                                onClick={() => setStreamsModalOpen(true)}>
                                <i className="fa-solid fa-bolt pe-2 text-danger"></i>
                                <span>
                                    Señal: <strong>{selectedStream?.title || `${streams.length} opciones`}</strong>
                                </span>
                                <i className="fa-solid fa-chevron-down ps-2"></i>
                            </button>
                        )}

                        {/* Viewed Toggle */}
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-btn ${item?.view ? "tv-btn--primary" : "tv-btn--glass"}`}
                            onClick={() => markThingAsView(!item?.view)}>
                            <i className={`fa-solid ${item?.view ? "fa-eye" : "fa-eye-slash"} pe-2`}></i>
                            {item?.view ? "Visto" : "Marcar visto"}
                        </button>

                        <TmdbLikeItem tmdbItem={item} forceDarkMode="dark" />

                        {type === "tv" && nextEpisode && onGoNextEpisode && (
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--primary"
                                onClick={onGoNextEpisode}>
                                Siguiente S{nextEpisode.season_number}:E{nextEpisode.episode_number}
                                <i className="fa-solid fa-forward-step ps-2"></i>
                            </button>
                        )}
                    </div>
                </header>

                {/* Bottom OSD Bar */}
                <footer className="tv-player-screen__osd-bottom" data-tv-row="tv-player-osd-bottom">
                    {/* Scrub Progress Bar */}
                    <div className="tv-player-screen__progress-container">
                        <span className="tv-player-screen__time-label">
                            {formatTime(currentTime)}
                        </span>
                        <div
                            className="tv-player-screen__progress-bar"
                            onClick={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                                if (duration > 0) {
                                    const target = ratio * duration;
                                    setVideoCurrentTime(target);
                                    triggerFlash(`Ir a ${formatTime(target)}`, "fa-forward");
                                }
                            }}>
                            <div
                                className="tv-player-screen__progress-fill"
                                style={{
                                    width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`
                                }}
                            />
                        </div>
                        <span className="tv-player-screen__time-label">
                            {duration > 0 ? `-${formatTime(Math.max(0, duration - currentTime))}` : "--:--"}
                            {" / "}
                            {formatTime(duration)}
                        </span>
                    </div>

                    {/* TV Controls Row */}
                    <div className="tv-player-screen__controls-row">
                        <div className="tv-player-screen__controls-group">
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--glass"
                                onClick={() => handleSeek(-10)}
                                title="Retroceder 10 segundos">
                                <i className="fa-solid fa-rotate-left pe-2"></i>-10s
                            </button>

                            <button
                                type="button"
                                data-tv-focusable="true"
                                data-tv-autofocus="true"
                                className="tv-btn tv-btn--primary tv-btn--lg"
                                onClick={togglePlayPause}
                                title={isPlaying ? "Pausar" : "Reproducir"}>
                                <i className={`fa-solid ${isPlaying ? "fa-pause" : "fa-play"} pe-2`}></i>
                                {isPlaying ? "Pausar" : "Reproducir"}
                            </button>

                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--glass"
                                onClick={() => handleSeek(10)}
                                title="Adelantar 10 segundos">
                                <i className="fa-solid fa-rotate-right pe-2"></i>+10s
                            </button>
                        </div>

                        <div className="tv-player-screen__controls-group">
                            {/* Subtitles Button */}
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className={`tv-btn ${currentSubtitle ? "tv-btn--primary" : "tv-btn--glass"}`}
                                onClick={() => setSubtitlesModalOpen(true)}>
                                <i className="fa-solid fa-closed-captioning pe-2"></i>
                                <span>
                                    Subtítulos:{" "}
                                    <strong>{currentSubtitle?.title || currentSubtitle?.lang || "Desactivados"}</strong>
                                </span>
                            </button>

                            {/* Audio Track Button */}
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--glass"
                                onClick={() => setAudiosModalOpen(true)}>
                                <i className="fa-solid fa-headphones pe-2"></i>
                                <span>
                                    Audio:{" "}
                                    <strong>
                                        {audios.find((a) => a.id === selectedAudioId)?.label ||
                                            audios.find((a) => a.id === selectedAudioId)?.language ||
                                            "Predeterminado"}
                                    </strong>
                                </span>
                            </button>

                            {/* Volume / Mute Button */}
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--glass tv-btn--icon"
                                onClick={() => {
                                    const nextMuted = !isMuted;
                                    setIsMuted(nextMuted);
                                    if (videoRef.current) videoRef.current.muted = nextMuted;
                                }}
                                title={isMuted ? "Desmutear" : "Mutear"}>
                                <i className={`fa-solid ${isMuted ? "fa-volume-xmark" : "fa-volume-high"}`}></i>
                            </button>

                            {/* Fullscreen Button */}
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className={`tv-btn tv-btn--glass tv-btn--icon ${isFullscreen ? "tv-btn--primary" : ""}`}
                                onClick={toggleFullscreen}
                                title={isFullscreen ? "Salir de pantalla completa (F)" : "Pantalla completa (F)"}>
                                <i className={`fa-solid ${isFullscreen ? "fa-compress" : "fa-expand"}`}></i>
                            </button>
                        </div>
                    </div>
                </footer>
            </div>

            {/* 4. Modals with Remote Spatial Navigation */}

            {/* Server Selector Modal */}
            <TvModal
                open={serversModalOpen}
                onClose={() => setServersModalOpen(false)}
                title="Seleccionar Servidor de Vídeo"
                subtitle="Elige la fuente de reproducción con las flechas del mando y pulsa OK">
                <div className="tv-modal-list">
                    {validServers.map((srv) => {
                        const isSelected = srv.value === selectedServer;
                        return (
                            <button
                                key={srv.value}
                                type="button"
                                data-tv-focusable="true"
                                data-tv-autofocus={isSelected ? "true" : undefined}
                                className={`tv-modal-option-btn ${isSelected ? "is-active" : ""}`}
                                onClick={() => {
                                    setSelectedServer(srv.value);
                                    setServersModalOpen(false);
                                }}>
                                <span>
                                    {srv.value === "SquadFlix" && <span className="pe-2">🌟</span>}
                                    {srv.flag && <span className={`${srv.flag} me-2`}></span>}
                                    {!srv.flag && srv.value !== "SquadFlix" && <span className="pe-2">🌐</span>}
                                    {srv.value}
                                </span>
                                {isSelected && <i className="fa-solid fa-check text-danger"></i>}
                            </button>
                        );
                    })}
                </div>
            </TvModal>

            {/* Streams / Torrents Selector Modal */}
            <TvModal
                open={streamsModalOpen}
                onClose={() => setStreamsModalOpen(false)}
                title="Señales y Torrents Disponibles"
                subtitle="Elige la señal o versión que prefieras reproducir">
                <div className="tv-modal-list">
                    {streams.map((st) => {
                        const isSelected = selectedStream?.id === st.id;
                        return (
                            <button
                                key={st.id}
                                type="button"
                                data-tv-focusable="true"
                                data-tv-autofocus={isSelected ? "true" : undefined}
                                className={`tv-modal-option-btn ${isSelected ? "is-active" : ""}`}
                                onClick={() => {
                                    selectStream(st);
                                    setStreamsModalOpen(false);
                                }}>
                                <div>
                                    <div className="d-flex align-items-center gap-2">
                                        {st.badge && <span className="tv-pill tv-pill--accent">{st.badge}</span>}
                                        <strong>{st.title}</strong>
                                    </div>
                                    {st.subtitle && (
                                        <small className="text-muted d-block mt-1">{st.subtitle}</small>
                                    )}
                                </div>
                                {isSelected && <i className="fa-solid fa-check text-danger"></i>}
                            </button>
                        );
                    })}
                </div>
            </TvModal>

            {/* Subtitles Selector Modal */}
            <TvModal
                open={subtitlesModalOpen}
                onClose={() => setSubtitlesModalOpen(false)}
                title="Subtítulos"
                subtitle="Elige la pista de subtítulos a mostrar">
                <div className="tv-modal-list">
                    <button
                        type="button"
                        data-tv-focusable="true"
                        data-tv-autofocus={!currentSubtitle ? "true" : undefined}
                        className={`tv-modal-option-btn ${!currentSubtitle ? "is-active" : ""}`}
                        onClick={() => {
                            setCurrentSubtitle(null);
                            setSubtitlesModalOpen(false);
                        }}>
                        <span>
                            <i className="fa-solid fa-ban pe-3 text-muted"></i>Desactivados
                        </span>
                        {!currentSubtitle && <i className="fa-solid fa-check text-danger"></i>}
                    </button>

                    {availableSubtitles.map((sub: any, idx: number) => {
                        const isSelected = currentSubtitle?.id === sub.id;
                        return (
                            <button
                                key={sub.id || idx}
                                type="button"
                                data-tv-focusable="true"
                                data-tv-autofocus={isSelected ? "true" : undefined}
                                className={`tv-modal-option-btn ${isSelected ? "is-active" : ""}`}
                                onClick={() => {
                                    setCurrentSubtitle(sub);
                                    setSubtitlesModalOpen(false);
                                }}>
                                <span>
                                    <span className={`fi fi-${sub.lang} me-3`}></span>
                                    {sub.title || sub.lang}
                                </span>
                                {isSelected && <i className="fa-solid fa-check text-danger"></i>}
                            </button>
                        );
                    })}
                </div>
            </TvModal>

            {/* Audio Tracks Selector Modal */}
            <TvModal
                open={audiosModalOpen}
                onClose={() => setAudiosModalOpen(false)}
                title="Pistas de Audio"
                subtitle="Elige el idioma del audio">
                <div className="tv-modal-list">
                    {audios.length === 0 ? (
                        <div className="p-4 text-center text-muted">
                            Solo hay una pista de audio disponible en esta señal.
                        </div>
                    ) : (
                        audios.map((aud: any, idx: number) => {
                            const isSelected = selectedAudioId === aud.id || (selectedAudioId == null && idx === 0);
                            return (
                                <button
                                    key={aud.id || idx}
                                    type="button"
                                    data-tv-focusable="true"
                                    data-tv-autofocus={isSelected ? "true" : undefined}
                                    className={`tv-modal-option-btn ${isSelected ? "is-active" : ""}`}
                                    onClick={() => handleAudioTrackChange(aud)}>
                                    <span>
                                        {aud.language && <span className={`fi fi-${aud.language} me-3`}></span>}
                                        {aud.label || aud.language || `Pista ${idx + 1}`}
                                    </span>
                                    {isSelected && <i className="fa-solid fa-check text-danger"></i>}
                                </button>
                            );
                        })
                    )}
                </div>
            </TvModal>

            {/* Video Mode Selector Modal (Streaming vs Lossless) */}
            <TvModal
                open={videoModeModalOpen}
                onClose={() => setVideoModeModalOpen(false)}
                title="Modo de Vídeo en TV"
                subtitle="Elige el modo de reproducción según la compatibilidad de tu televisor">
                <div className="tv-modal-list">
                    <button
                        type="button"
                        data-tv-focusable="true"
                        data-tv-autofocus={videoMode === "streaming" ? "true" : undefined}
                        className={`tv-modal-option-btn ${videoMode === "streaming" ? "is-active" : ""}`}
                        onClick={() => handleSelectVideoMode("streaming")}>
                        <div className="d-flex flex-column text-start">
                            <span className="fw-bold fs-5">
                                <i className="fa-solid fa-bolt text-warning me-2"></i>
                                Streaming (Mayor Compatibilidad)
                            </span>
                            <span className="text-white-50 small mt-1">
                                Transcodificación en tiempo real. Máxima compatibilidad de audio y vídeo con Smart TVs.
                            </span>
                        </div>
                        {videoMode === "streaming" && <i className="fa-solid fa-check text-danger fs-4 ms-3"></i>}
                    </button>

                    <button
                        type="button"
                        data-tv-focusable="true"
                        data-tv-autofocus={videoMode === "lossless" ? "true" : undefined}
                        className={`tv-modal-option-btn ${videoMode === "lossless" ? "is-active" : ""}`}
                        onClick={() => handleSelectVideoMode("lossless")}>
                        <div className="d-flex flex-column text-start">
                            <span className="fw-bold fs-5">
                                <i className="fa-solid fa-gem text-info me-2"></i>
                                Lossless (Calidad Original)
                            </span>
                            <span className="text-white-50 small mt-1">
                                Reproducción directa del archivo original. Máxima calidad y carga instantánea (requiere que la TV soporte el formato).
                            </span>
                        </div>
                        {videoMode === "lossless" && <i className="fa-solid fa-check text-danger fs-4 ms-3"></i>}
                    </button>
                </div>
            </TvModal>

            {type === "tv" && item?.serie && <SpoilersPreferenceModal serie={item.serie} />}
        </div>
    );
};

export const SquadflixTvEpisodeDetails: React.FC<{squadflixTvHook?: any}> = ({squadflixTvHook}) => {
    const intl = useIntl();
    const {serieId, seasonNumber, episodeNumber} = useParams();
    const {
        serie,
        episode,
        episodeItem,
        nextEpisode,
        goToNextEpisode,
        loading
    } = useEpisodeDetailsData(serieId!, seasonNumber!, episodeNumber!, squadflixTvHook);

    if (loading) {
        return (
            <div className="tv-loading-center">
                <Spinner />
            </div>
        );
    }

    const tagline =
        intl.formatMessage(
            {id: "tmdb-item-details.aired-date"},
            {date: new Date(episode.air_date).toLocaleDateString()}
        ) +
        " - " +
        episode.runtime +
        "min";

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>{`${serie.name} - S${episode.season_number}:E${episode.episode_number}`}</title>
            </Helmet>
            <SquadflixTvPlayerView
                view={episode.view}
                item={episodeItem}
                itemName={episode.name}
                overview={episode.overview}
                imagePath={episode.still_path}
                playing={true}
                file={episode.shared_files?.[0]}
                type="tv"
                squadFlixTvHook={squadflixTvHook}
                tmdbId={serieId!}
                season={episode.season_number}
                episode={episode.episode_number}
                nextEpisode={nextEpisode}
                onGoNextEpisode={goToNextEpisode}
                tagline={tagline}
            />
        </>
    );
};

export default SquadflixTvPlayerView;
